import { WebsocketEvents, WebsocketEventTypes } from "./types";

const socketMap: Record<string, CustomWebSocket> = {};

// Keepalive. The server answers these from its Durable Object via
// setWebSocketAutoResponse, so they never wake it from hibernation. Without
// traffic on the wire an idle socket gets silently reaped by intermediate
// network gear and neither end ever sees a close.
const PING_MESSAGE = "ping";
const PONG_MESSAGE = "pong";
const PING_INTERVAL_MS = 25_000;
// Force a reconnect once this long has passed with no pong. Three missed pings.
const PONG_TIMEOUT_MS = 80_000;

// Backoff ceiling. There is deliberately no attempt cap: the dashboard used to
// give up after 5 tries (~31s), after which the online/offline indicator froze
// at whatever it last saw until the page was reloaded.
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

class CustomWebSocket {
  private url: string;
  private reconnectAttempts: number = 0;
  private reconnectTimer?: NodeJS.Timeout;
  private pingTimer?: ReturnType<typeof setInterval>;
  private lastPongAt: number = Date.now();
  private closedByUs: boolean = false;

  public ws!: WebSocket;
  public onOpen: ((this: WebSocket, ev: Event) => any) | null = null;
  public onMessage: ((this: WebSocket, ev: MessageEvent) => any) | null = null;
  public onError: ((this: WebSocket, ev: Event) => any) | null = null;
  public onClose: ((this: WebSocket, ev: CloseEvent) => any) | null = null;

  public onCallback: {
    [x: string]: (data?: any) => void;
  } = {};

  public get isOpen() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  public get isConnecting() {
    return this.ws?.readyState === WebSocket.CONNECTING;
  }

  public static getInstance(url: string) {
    const existing = socketMap[url];
    // Also reuse a socket that is still connecting; checking only for OPEN meant
    // a second caller during the handshake built a duplicate connection and
    // orphaned the first one.
    if (existing && (existing.isOpen || existing.isConnecting)) return existing;
    socketMap[url] = new CustomWebSocket(url);
    return socketMap[url];
  }

  private constructor(url: string) {
    this.url = url;
    this.connect();
  }

  private connect() {
    this.closedByUs = false;
    this.ws = new WebSocket(this.url);

    // NOTE: the public handlers are deliberately NOT re-read from the raw socket
    // here. A freshly constructed WebSocket has null handlers, so the previous
    // `this.onOpen = this.ws.onopen` wiped the consumer's handlers on every
    // reconnect — the dashboard would reconnect, never re-run joinRooms, and
    // silently stop receiving presence updates for the rest of the session.

    this.ws.onopen = (ev) => {
      this.reconnectAttempts = 0; // Reset attempts on successful connection
      this.lastPongAt = Date.now();
      this.startKeepalive();
      this.onOpen && this.onOpen.call(this.ws, ev);
    };

    this.ws.onmessage = this.onMessageCallback;

    this.ws.onerror = (ev) => {
      this.onError && this.onError.call(this.ws, ev);
    };

    this.ws.onclose = (ev) => {
      this.stopKeepalive();
      this.onClose && this.onClose.call(this.ws, ev);
      if (!this.closedByUs) this.tryReconnect();
    };
  }

  private startKeepalive() {
    this.stopKeepalive();

    this.pingTimer = setInterval(() => {
      if (Date.now() - this.lastPongAt > PONG_TIMEOUT_MS) {
        // Turn a silently dead connection into a close event, which is what
        // drives the reconnect.
        this.stopKeepalive();
        try {
          this.ws.close(4000, "Keepalive timeout");
        } catch {
          /* already gone; onclose still fires */
        }
        return;
      }

      if (this.ws.readyState !== WebSocket.OPEN) return;

      try {
        this.ws.send(PING_MESSAGE);
      } catch {
        /* the timeout above handles recovery */
      }
    }, PING_INTERVAL_MS);
  }

  private stopKeepalive() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = undefined;
    }
  }

  private tryReconnect() {
    if (this.reconnectTimer) return;

    const capped = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts),
      RECONNECT_MAX_MS,
    );
    // Full jitter, so every open dashboard doesn't retry on the same tick after
    // a server restart.
    const delay = Math.random() * capped;
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delay);
  }

  public close() {
    this.closedByUs = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.stopKeepalive();
    this.ws.close();
  }

  onMessageCallback = (ev: MessageEvent) => {
    // The keepalive reply is a bare string, not JSON. Handle it first so it
    // never reaches application code.
    if (ev.data === PONG_MESSAGE) {
      this.lastPongAt = Date.now();
      return;
    }

    try {
      const data = JSON.parse(ev.data);
      const type = data?.type ?? "";
      const event = data?.event ?? "";

      if (type !== "callback") {
        this.onMessage && this.onMessage.call(this.ws, ev);
      } else {
        // Guard the lookup: a callback frame for an event with no registered
        // handler used to throw and get mis-routed into onMessage.
        if (event && typeof this.onCallback[event] === "function") {
          this.onCallback[event](data.data);
        }
      }
    } catch (e) {
      this.onMessage && this.onMessage.call(this.ws, ev);
    }
  };

  public emit<T extends keyof typeof WebsocketEvents>(
    event: T,
    data: WebsocketEventTypes[T]["request"],
    cb?: (data?: WebsocketEventTypes[T]["response"]) => void,
  ) {
    try {
      if (cb) this.onCallback[event] = cb;

      if (this.ws.readyState !== WebSocket.OPEN) return;

      this.ws.send(
        JSON.stringify({
          event: event,
          data: data,
        }),
      );
    } catch { }
  }
}

export default CustomWebSocket;
